import { AuthorId, CorpusId } from '../../models/aliases';
import { events } from '../../analytics/events';
import { formatAuthorName } from '../../utils/text';
import { useTrackedHandler } from '../../hooks/useTracking';

import { Link, Typography } from '@mui/material';
import styled from 'styled-components';

const authorLinkPrefix = 'https://www.semanticscholar.org/author/';
type AuthorName = string;

interface AuthorChipProps {
  chipContent: AuthorName | JSX.Element;
  corpusId: CorpusId;
  authorId?: AuthorId;
  shorten?: boolean;
  onClick?: () => void;
}
// This component is used to render the author name with a link if an id is provided
export function AuthorChip(props: AuthorChipProps): JSX.Element {
  const { chipContent, corpusId, authorId, shorten, onClick } = props;

  const reportTrackingOnClick = useTrackedHandler(
    events.navigation.PaperAuthorClicked,
    (): void => {},
    () => ({ corpusId, authorId: authorId ?? -1 }),
  );

  let content: JSX.Element;
  if (typeof chipContent === 'string') {
    content = <>{formatAuthorName(chipContent, { shorten })}</>;
  } else {
    content = chipContent;
  }
  // If an id is provided, render the author name as a link
  if (authorId !== undefined) {
    return (
      <AuthorLink
        href={`${authorLinkPrefix}${authorId}`}
        target="_blank"
        underline="hover"
        onClick={reportTrackingOnClick}
      >
        <AuthorTypo>{content}</AuthorTypo>
      </AuthorLink>
    );
  } else if (onClick) {
    // If an id is not provided, but onClick is provided, render the chip content as a button
    return (
      <AuthorLink onClick={onClick}>
        <AuthorTypo>{content}</AuthorTypo>
      </AuthorLink>
    );
  }

  // If no id, just render the name
  return <AuthorTypo>{content}</AuthorTypo>;
}

const AuthorTypo = styled(Typography).attrs({
  component: 'span',
  variant: 'body2',
})`
  padding: 0.5rem 1rem; // px: 1, py: 0.5 converted to padding
  border-radius: ${({ theme }) => theme.border.radius.medium};
  line-height: 1.1;
  font-size: ${({ theme }) => theme.font.size.small};
  background-color: ${({ theme }) => theme.color.background.paper.secondary};
  text-transform: capitalize;
  margin: 0;
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: 400;
  letter-spacing: 0rem;
  padding: 4px 8px;
`;

const AuthorLink = styled(Link)`
  &.MuiLink-root {
    cursor: pointer;
    color: rgb(250, 242, 233);
    &:hover {
      color: rgba(250, 242, 233, 0.9);
    }
  }
  &.MuiLink-root.MuiTypography-root:hover {
    color: rgba(250, 242, 233, 0.9);
  }
`;
